import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import subscriptionService from '../../services/subscriptionService';
import SubscriptionTiers from './SubscriptionTiers';
import PaymentForm from './PaymentForm';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 36px;
  color: #2b3d5c;
  margin: 0 0 10px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #666;
  margin: 0;
`;

const CurrentPlan = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 40px;
`;

const PlanDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PlanInfo = styled.div`
  h3 {
    margin: 0 0 5px 0;
    color: #2b3d5c;
  }

  p {
    margin: 0;
    color: #666;
  }
`;

const UsageInfo = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #dee2e6;
`;

const UsageMetric = styled.div`
  text-align: center;

  h4 {
    margin: 0 0 5px 0;
    color: #0062cc;
    font-size: 24px;
  }

  p {
    margin: 0;
    color: #666;
    font-size: 14px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  width: 100%;
  max-width: 500px;
`;

const ModalTitle = styled.h2`
  margin: 0 0 20px 0;
  color: #2b3d5c;
`;

const SubscriptionManager = () => {
  const [tiers, setTiers] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      const [tiersData, currentSub] = await Promise.all([
        subscriptionService.getSubscriptionTiers(),
        subscriptionService.getCurrentSubscription()
      ]);
      setTiers(tiersData);
      setCurrentSubscription(currentSub);
    } catch (error) {
      console.error('Error loading subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTierSelect = (tier) => {
    setSelectedTier(tier);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async (paymentMethodId) => {
    try {
      setLoading(true);
      await subscriptionService.createSubscription(
        selectedTier.id,
        paymentMethodId
      );
      await loadSubscriptionData();
      setShowPaymentModal(false);
    } catch (error) {
      console.error('Error creating subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    try {
      setLoading(true);
      await subscriptionService.cancelSubscription(currentSubscription.id);
      await loadSubscriptionData();
    } catch (error) {
      console.error('Error canceling subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Subscription Management</Title>
        <Subtitle>Choose the plan that works best for your business</Subtitle>
      </Header>

      {currentSubscription && (
        <CurrentPlan>
          <PlanDetails>
            <PlanInfo>
              <h3>{currentSubscription.tier_name}</h3>
              <p>
                {currentSubscription.status === 'trialing' ? 
                  'Trial ends' : 'Renews'} on{' '}
                {new Date(currentSubscription.current_period_end).toLocaleDateString()}
              </p>
            </PlanInfo>
            <button onClick={handleCancelSubscription}>
              Cancel Subscription
            </button>
          </PlanDetails>

          <UsageInfo>
            <UsageMetric>
              <h4>{currentSubscription.properties_count}</h4>
              <p>Active Properties</p>
            </UsageMetric>
            <UsageMetric>
              <h4>{currentSubscription.property_limit}</h4>
              <p>Property Limit</p>
            </UsageMetric>
          </UsageInfo>
        </CurrentPlan>
      )}

      <SubscriptionTiers
        tiers={tiers}
        currentTier={currentSubscription?.tier_id}
        onSelectTier={handleTierSelect}
        loading={loading}
      />

      {showPaymentModal && (
        <ModalOverlay>
          <Modal>
            <ModalTitle>Complete Subscription</ModalTitle>
            <Elements stripe={stripePromise}>
              <PaymentForm
                tier={selectedTier}
                onSuccess={handlePaymentSuccess}
                onCancel={() => setShowPaymentModal(false)}
              />
            </Elements>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default SubscriptionManager;